import React, { useState, useEffect } from 'react';
import { FileText, Download, BarChart3, PawPrint, Shield, TrendingUp, Package, AlertTriangle, Baby, Filter } from 'lucide-react';
import api from '../utils/api';
import { formatDateBR } from '../utils/formatDate';
import toast from 'react-hot-toast';
import AnimalSearchSelect from '../components/AnimalSearchSelect';

const API_BASE = '/api'; // para links de download direto

export default function Relatorios() {
  const [animais, setAnimais] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [racas, setRacas] = useState([]);
  const [filtros, setFiltros] = useState({ animal_id: '', lote_id: '', raca_id: '', data_inicio: '', data_fim: '' });

  useEffect(() => {
    api.get('/animais', { params: { situacao: 'ativo', limit: 500 } }).then(r => setAnimais(r.data.data || r.data || []));
    api.get('/lotes').then(r => setLotes(r.data || []));
    api.get('/racas').then(r => setRacas(r.data || []));
  }, []);

  const downloadPDF = (rota, params = {}) => {
    const token = localStorage.getItem('token');
    const query = new URLSearchParams(params).toString();
    const url = `${API_BASE}/relatorios/pdf/${rota}${query ? '?' + query : ''}`;
    // Usar fetch para enviar o token
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error('Erro ao gerar PDF');
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${rota.replace(/\//g, '_')}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success('PDF gerado!');
      })
      .catch(() => toast.error('Erro ao gerar PDF'));
  };

  const downloadCSV = (rota, params = {}) => {
    const token = localStorage.getItem('token');
    const query = new URLSearchParams(params).toString();
    const url = `${API_BASE}/relatorios/csv/${rota}${query ? '?' + query : ''}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error('Erro ao gerar CSV');
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${rota}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success('CSV exportado!');
      })
      .catch(() => toast.error('Erro ao gerar CSV'));
  };

  const f = (k, v) => setFiltros(p => ({ ...p, [k]: v }));

  const relatorios = [
    {
      titulo: 'Ficha Completa do Animal',
      descricao: 'Dados cadastrais, filiação, pesagens, intervenções, coberturas, partos e amamentação em um único documento.',
      icone: PawPrint,
      cor: '#2D6A4F',
      filtroAnimal: true,
      acoes: [
        { label: 'PDF', onClick: () => {
          if (!filtros.animal_id) return toast.error('Selecione um animal');
          downloadPDF(`ficha-animal/${filtros.animal_id}`);
        }},
      ],
    },
    {
      titulo: 'GMD — Curva de Crescimento',
      descricao: 'Ganho médio diário. Geral (todos os animais) ou individual com histórico de pesagens.',
      icone: TrendingUp,
      cor: '#2563EB',
      filtroAnimal: true,
      filtroLote: true,
      acoes: [
        { label: 'PDF Geral', onClick: () => downloadPDF('gmd', { lote_id: filtros.lote_id, raca_id: filtros.raca_id }) },
        { label: 'PDF por Animal', onClick: () => {
          if (!filtros.animal_id) return toast.error('Selecione um animal');
          downloadPDF('gmd', { animal_id: filtros.animal_id });
        }},
      ],
    },
    {
      titulo: 'Rebanho',
      descricao: 'Lista completa do rebanho ativo com resumo por categoria, raça, peso e idade.',
      icone: PawPrint,
      cor: '#16A34A',
      acoes: [
        { label: 'PDF', onClick: () => downloadPDF('rebanho') },
        { label: 'CSV', onClick: () => {
          // Usa a rota existente /relatorios/rebanho
          api.get('/relatorios/rebanho').then(r => {
            const data = r.data;
            const headers = ['Brinco', 'Nome', 'Sexo', 'Raça', 'Categoria', 'Lote', 'Idade (dias)', 'Peso Atual'];
            const rows = data.map(a => [a.brinco, a.nome || '', a.sexo, a.raca_nome || '', a.categoria || '', a.lote_nome || '', a.idade_dias || '', a.peso_atual || '']);
            const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
            const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = 'rebanho.csv';
            a.click();
            toast.success('CSV exportado!');
          }).catch(() => toast.error('Erro'));
        }},
      ],
    },
    {
      titulo: 'Sanitário',
      descricao: 'Histórico de intervenções sanitárias. Geral ou por animal, com filtro por período.',
      icone: Shield,
      cor: '#DC2626',
      filtroAnimal: true,
      filtroPeriodo: true,
      acoes: [
        { label: 'PDF Geral', onClick: () => downloadPDF('sanitario', { data_inicio: filtros.data_inicio, data_fim: filtros.data_fim }) },
        { label: 'PDF por Animal', onClick: () => {
          if (!filtros.animal_id) return toast.error('Selecione um animal');
          downloadPDF('sanitario', { animal_id: filtros.animal_id, data_inicio: filtros.data_inicio, data_fim: filtros.data_fim });
        }},
        { label: 'CSV', onClick: () => {
          api.get('/relatorios/sanitario', { params: { data_inicio: filtros.data_inicio, data_fim: filtros.data_fim } }).then(r => {
            const data = r.data;
            const headers = ['Data', 'Animal', 'Tipo', 'Produto', 'Dose', 'Carência', 'Status'];
            const rows = data.map(i => [formatDateBR(i.data), i.brinco, i.tipo, i.produto_nome || '', i.dose || '', i.carencia_dias || '', i.status || '']);
            const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
            const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = 'sanitario.csv';
            a.click();
            toast.success('CSV exportado!');
          }).catch(() => toast.error('Erro'));
        }},
      ],
    },
    {
      titulo: 'Índices Zootécnicos',
      descricao: 'Natalidade, mortalidade, taxa de prenhez, média de crias, GMD médio do rebanho.',
      icone: BarChart3,
      cor: '#7C3AED',
      acoes: [
        { label: 'PDF', onClick: () => downloadPDF('indices') },
        { label: 'CSV', onClick: () => downloadCSV('indices') },
      ],
    },
    {
      titulo: 'Estoque & Fórmulas de Ração',
      descricao: 'Lista completa de insumos, matérias-primas e fórmulas de ração cadastradas.',
      icone: Package,
      cor: '#D97706',
      acoes: [
        { label: 'PDF', onClick: () => downloadPDF('estoque') },
        { label: 'CSV', onClick: () => downloadCSV('estoque') },
      ],
    },
    {
      titulo: 'Estoque Crítico — Lista de Compras',
      descricao: 'Produtos abaixo do estoque mínimo com sugestão de quantidade para reposição.',
      icone: AlertTriangle,
      cor: '#EA580C',
      acoes: [
        { label: 'PDF', onClick: () => downloadPDF('estoque-critico') },
        { label: 'CSV', onClick: () => downloadCSV('estoque-critico') },
      ],
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div><h1>Relatórios</h1><p>Exporte relatórios em PDF (com logo e tema) ou CSV</p></div>
      </div>

      {/* Filtros globais */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Filter size={14} /> Filtros para os relatórios
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 2, minWidth: 200 }}>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Animal</label>
            <AnimalSearchSelect value={filtros.animal_id} onChange={id => f('animal_id', id)} emptyLabel="Todos / Geral" />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Lote</label>
            <select value={filtros.lote_id} onChange={e => f('lote_id', e.target.value)} style={{ width: 'auto' }}>
              <option value="">Todos</option>
              {lotes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Raça</label>
            <select value={filtros.raca_id} onChange={e => f('raca_id', e.target.value)} style={{ width: 'auto' }}>
              <option value="">Todas</option>
              {racas.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>De</label>
            <input type="date" value={filtros.data_inicio} onChange={e => f('data_inicio', e.target.value)} style={{ width: 150 }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Até</label>
            <input type="date" value={filtros.data_fim} onChange={e => f('data_fim', e.target.value)} style={{ width: 150 }} />
          </div>
        </div>
      </div>

      {/* Cards de relatórios */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
        {relatorios.map((rel, i) => {
          const Icon = rel.icone;
          return (
            <div key={i} className="card" style={{ borderTop: `3px solid ${rel.cor}`, padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                <div style={{ padding: 10, borderRadius: 10, background: rel.cor + '15', flexShrink: 0 }}>
                  <Icon size={22} color={rel.cor} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{rel.titulo}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.4 }}>{rel.descricao}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {rel.acoes.map((acao, j) => (
                  <button key={j} className="btn btn-secondary" onClick={acao.onClick}
                    style={{ fontSize: 12, padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Download size={13} /> {acao.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
